/**
 * Day 5: Visual Calendar V2 — Month Grid Helper
 * Pure functions for building month calendar grid structure.
 * 
 * Rules:
 * - Pure functions only — no API calls, no side effects
 * - No mutation of shift objects
 * - Builds full visible grid range (including leading/trailing days)
 */

import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, format, isSameMonth, isToday } from 'date-fns';

/**
 * Build the full month grid — 6 rows x 7 columns of day cells.
 * Includes leading days from prev month and trailing days from next month.
 * @param {Date} currentDate - Any date in the target month
 * @returns {{ cells: Array<{date: Date, dateStr: string, isCurrentMonth: boolean, isToday: boolean}>, gridStart: Date, gridEnd: Date }}
 */
export function buildMonthCells(currentDate) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const cells = [];
  let day = gridStart;
  while (day <= gridEnd) {
    cells.push({
      date: day,
      dateStr: format(day, 'yyyy-MM-dd'),
      isCurrentMonth: isSameMonth(day, currentDate),
      isToday: isToday(day),
    });
    day = addDays(day, 1);
  }

  return { cells, gridStart, gridEnd };
}

/**
 * Group shifts by date string for fast lookup.
 * @param {Array} shifts - Array of shift objects with .date field (YYYY-MM-DD)
 * @returns {Map<string, Array>} Map of dateStr → shifts array
 */
export function groupShiftsByDate(shifts) {
  const map = new Map();
  for (const shift of shifts) {
    const key = shift.date;
    if (!key) continue;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(shift);
  }
  return map;
}

/**
 * Get compact status summary for a day's shifts.
 * @param {Array} dayShifts - Shifts for one day
 * @returns {{ total: number, open: number, partial: number, assigned: number, other: number }}
 */
export function getDayStatusSummary(dayShifts) {
  let open = 0, partial = 0, assigned = 0, other = 0;
  for (const s of dayShifts) {
    switch (s.status) {
      case 'OPEN': open++; break;
      case 'PARTIALLY_FILLED': partial++; break;
      case 'ASSIGNED': case 'CONFIRMED': assigned++; break;
      default: other++; break;
    }
  }
  return { total: dayShifts.length, open, partial, assigned, other };
}
